import React, { useState, useRef, useCallback } from 'react';
import './HeroBulb.css';

const CHIPS = [
  { label: 'Full Stack Dev',   cls: 'chip-a' },
  { label: 'Machine Learning', cls: 'chip-b' },
  { label: 'Data Science',     cls: 'chip-c' },
  { label: 'Deployment',       cls: 'chip-d' },
];

const HeroBulb = ({ profileImg, profileImgSm, profileImgAvif, profileImgAvifSm }) => {
  const sceneRef = useRef(null);
  const rafRef   = useRef(null);
  const rectRef  = useRef(null); // cached bounds — avoids layout read per mousemove

  const [tilt,    setTilt]    = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);
  const [ripple,  setRipple]  = useState(false);

  /* Cache the scene rect on enter (and drop it on leave) so the move handler
     never triggers a forced synchronous layout/reflow. */
  const handleEnter = useCallback(() => {
    setHovered(true);
    if (sceneRef.current) rectRef.current = sceneRef.current.getBoundingClientRect();
  }, []);

  /* ── 3-D tilt follows mouse ─────────────────── */
  const handleMouseMove = useCallback((e) => {
    const rect = rectRef.current;
    if (!rect) return;
    const dx = (e.clientX - (rect.left + rect.width  / 2)) / (rect.width  / 2);
    const dy = (e.clientY - (rect.top  + rect.height / 2)) / (rect.height / 2);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() =>
      setTilt({ x: dy * -10, y: dx * 10 })
    );
  }, []);

  const handleLeave = useCallback(() => {
    setHovered(false);
    rectRef.current = null;
    setTilt({ x: 0, y: 0 });
  }, []);

  /* ── Click ripple ───────────────────────────── */
  const handleClick = useCallback(() => {
    setRipple(false);
    requestAnimationFrame(() => {
      setRipple(true);
      setTimeout(() => setRipple(false), 700);
    });
  }, []);

  return (
    <div
      ref={sceneRef}
      className={`av-scene${hovered ? ' av-hovered' : ''}`}
      style={{
        transform: `perspective(900px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        transition: hovered
          ? 'transform 0.12s linear'
          : 'transform 0.7s cubic-bezier(0.34,1.3,0.64,1)',
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={handleClick}
      aria-label="Obaidullah Zeeshan — Software Engineer"
    >
      {/* ── Ambient pulse glow ───────────────────── */}
      <div className="av-ambient" aria-hidden="true" />

      {/* ── Three orbital rings ──────────────────── */}
      <div className="av-ring ring-a" aria-hidden="true"><div className="av-dot" /></div>
      <div className="av-ring ring-b" aria-hidden="true"><div className="av-dot dot-b" /></div>
      <div className="av-ring ring-c" aria-hidden="true"><div className="av-dot dot-c" /></div>

      {/* ── Profile sphere ───────────────────────── */}
      <div className="av-sphere">
        <picture>
          {profileImgAvif && (
            <source
              type="image/avif"
              srcSet={profileImgAvifSm ? `${profileImgAvifSm} 280w, ${profileImgAvif} 560w` : profileImgAvif}
              sizes="(max-width: 480px) 192px, 270px"
            />
          )}
          <img
            src={profileImg}
            srcSet={profileImgSm ? `${profileImgSm} 280w, ${profileImg} 560w` : undefined}
            sizes="(max-width: 480px) 192px, 270px"
            alt="Obaidullah Zeeshan – Full-Stack Developer and AI Engineer"
            className="av-photo"
            width="270"
            height="270"
            fetchPriority="high"
            decoding="async"
            draggable="false"
          />
        </picture>
        <div className="av-shine" />
      </div>

      {/* ── Click ripple (outside sphere so it expands freely) ── */}
      {ripple && <div className="av-ripple" aria-hidden="true" />}

      {/* ── Floating tech chips ──────────────────── */}
      {CHIPS.map(c => (
        <div key={c.cls} className={`av-chip ${c.cls}`} aria-hidden="true">
          {c.label}
        </div>
      ))}

      {/* ── Availability badge ───────────────────── */}
      <div className="av-badge" aria-label="Available for work">
        <span className="av-badge-dot" />
        <span>Available for work</span>
      </div>
    </div>
  );
};

export default HeroBulb;
